/**
 * EmailKillSwitch — admin emergency control to stop / resume ALL notification email (Spec 255).
 *
 * One click sets `alert_email_enabled` (+ the engine master) off in the DB; because the send
 * gates read settings fresh each sweep tick (no cache), every email stream — escalation, digest,
 * pipeline, resolve-nudge, KPI — stops within about a minute, with no restart or container
 * shutdown. Resuming is guarded by a confirm (re-enabling can send a backlog digest).
 *
 * DD-890: SuperAdmin only (owner, 2026-10-01). The card sits on the Users page, which the tenant
 * Admin can now open for email preferences, so it gates itself on the area its API lives in —
 * internal.configuration (/api/settings) — and the fetch inside never runs for anyone refused.
 */
import React from 'react';
import { Mail, MailX, AlertTriangle } from 'lucide-react';
import { Card, Button, Badge, Modal } from '@/components/ui';
import { AccessGate } from '../access/AccessGate';
import {
  getEmailNotificationsEnabled,
  setEmailNotificationsEnabled,
} from '../../services/emailControlApi';

export const EmailKillSwitch: React.FC = () => (
  <AccessGate area="internal.configuration">
    <EmailKillSwitchCard />
  </AccessGate>
);

const EmailKillSwitchCard: React.FC = () => {
  const [enabled, setEnabled] = React.useState<boolean | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmResume, setConfirmResume] = React.useState(false);

  const load = React.useCallback(() => {
    getEmailNotificationsEnabled()
      .then(setEnabled)
      .catch(() => setError('Could not read the current email state.'));
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);

  const apply = async (value: boolean): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await setEmailNotificationsEnabled(value);
      setEnabled(value);
    } catch {
      setError('Failed to update — please try again.');
      load();
    } finally {
      setSaving(false);
      setConfirmResume(false);
    }
  };

  const on = enabled === true;

  return (
    <Card
      className={`mb-6 border-l-4 p-4 ${
        on ? 'border-l-[var(--color-status-success)]' : 'border-l-[var(--color-status-danger)]'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          {on ? (
            <Mail className="mt-0.5 h-5 w-5 text-[var(--color-status-success)]" />
          ) : (
            <MailX className="mt-0.5 h-5 w-5 text-[var(--color-status-danger)]" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                Email notifications
              </h3>
              {enabled === null ? (
                <span className="text-xs text-[var(--color-text-secondary)]">checking…</span>
              ) : on ? (
                <Badge variant="success">ON — sending</Badge>
              ) : (
                <Badge variant="danger">OFF — silenced</Badge>
              )}
            </div>
            <p className="mt-1 max-w-prose text-sm text-[var(--color-text-secondary)]">
              Master switch for every notification email — escalation, new-alert digest, pipeline,
              resolve reminders, and KPI. Turning it off stops all outgoing email within about a
              minute. No restart, no shutdown.
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {on ? (
            <Button
              variant="danger"
              isLoading={saving}
              disabled={enabled === null}
              leftIcon={<MailX className="h-4 w-4" />}
              onClick={() => apply(false)}
            >
              Stop all emails
            </Button>
          ) : (
            <Button
              variant="primary"
              isLoading={saving}
              disabled={enabled === null}
              leftIcon={<Mail className="h-4 w-4" />}
              onClick={() => setConfirmResume(true)}
            >
              Resume emails
            </Button>
          )}
        </div>
      </div>
      {error && <p className="mt-2 text-sm text-[var(--color-status-danger)]">{error}</p>}

      <Modal isOpen={confirmResume} onClose={() => setConfirmResume(false)} title="Resume email notifications?">
        <div className="space-y-4">
          <div className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-status-warning)]" />
            <p>
              This re-enables ALL notification email streams. If there is a backlog of open alerts,
              the next digest or escalation sweep may send a batch of emails. Continue?
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmResume(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" isLoading={saving} onClick={() => apply(true)}>
              Resume emails
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default EmailKillSwitch;
